import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import Navigation from '../components/Navigation';
import { Calendar, FileText, Users, RefreshCw, Clock } from 'lucide-react';
import TimeTracker from '../components/TimeTracker';
import { toast } from '../lib/toast';

type Task = Database['public']['Tables']['tasks']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];

interface TaskDetailsPageProps {
  navigate: (to: string) => void;
}

export default function TaskDetailsPage({ navigate }: TaskDetailsPageProps) {
  const [task, setTask] = useState<Task | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const taskId = window.location.pathname.split('/tasks/')[1];
    if (taskId) {
      fetchTask(taskId);
    } else {
      navigate('/tasks');
    }
  }, [retryCount]);

  const fetchTask = async (taskId: string) => {
    try {
      setError(null);
      setLoading(true);
      
      // First verify the user has access to this task
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Use Promise.all to fetch task and assignments in parallel
      const [taskResult, assignmentsResult] = await Promise.all([
        supabase
          .from('tasks')
          .select('*')
          .eq('id', taskId)
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('task_assignments')
          .select('team_members(*)')
          .eq('task_id', taskId)
      ]);

      if (taskResult.error) {
        if (taskResult.error.code === 'PGRST116') {
          throw new Error('You do not have permission to view this task');
        }
        throw taskResult.error;
      }

      if (!taskResult.data) {
        throw new Error('Task not found');
      }

      setTask(taskResult.data);
      
      // Ensure assignmentsResult.data exists before mapping
      const teamMembersList = assignmentsResult.data
        ? assignmentsResult.data
        .map(assignment => assignment.team_members)
        .filter((member): member is TeamMember => member !== null)
        : [];

      setTeamMembers(teamMembersList);
      setError(null);

    } catch (error) {
      console.error('Error fetching task:', error);
      const message = error instanceof Error ? error.message : 'Failed to load task details';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Normal': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'High': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200';
      case 'Urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation
          onSignOut={async () => await supabase.auth.signOut()}
          tasks={[]}
          currentView="all"
          onViewChange={() => {}}
          navigate={navigate}
        />
        <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          </div>
        </main>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {error ? (
            <div className="bg-red-50 dark:bg-red-900/50 p-4 rounded-lg mb-6">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
              <div className="mt-4 flex space-x-4">
                <button
                  onClick={handleRetry}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/tasks')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back to Tasks
              </button>
              </div>
            </div>
          ) : (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-start mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{task.title}</h1>
                <button
                  onClick={() => navigate('/tasks')}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
                >
                  Back to Tasks
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {task.due_date && (
                    <span className="flex items-center text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 px-2.5 py-0.5 rounded-full">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>

                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: task.description || '' }} />
                </div>

                {task.attachment_url && (
                  <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <a
                      href={task.attachment_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 hover:underline"
                    >
                      {task.attachment_name || 'Attachment'}
                    </a>
                  </div>
                )}

                {teamMembers.length > 0 && (
                  <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700/50 p-2 rounded-lg">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {teamMembers.map(member => member.name).join(', ')}
                    </span>
                  </div>
                )}
              </div>

              {/* Time Tracking Section */}
              <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-8">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Time Tracking
                </h2>
                <TimeTracker taskId={task.id} />
              </div>

            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}